import { formatElapsed } from "../timesheet";
import type { ReadyCount, TaskSegment } from "../timesheet";

import styles from "./SummaryBand.module.css";

interface SummaryBandProps {
  totalMs: number;
  runningCount: number;
  pausedCount: number;
  segments: readonly TaskSegment[];
  ready: ReadyCount;
  /** A publish is in flight. The page still passes it. The band ignores it. */
  publishing?: boolean;
  /** The publish press. The page still passes it. The band ignores it. */
  onPublish?: () => void;
  /** The shown day's label: `Today`, `Yesterday`, or a date such as `Fri 18 Sep`. */
  dayLabel?: string | undefined;
}

const RELATIVE_DAYS: readonly string[] = ["Today", "Yesterday", "Tomorrow"];

/** `Tracked today`, `Tracked yesterday`, or `Tracked on Fri 18 Sep`. */
function trackedHeading(dayLabel: string | undefined): string {
  if (dayLabel === undefined) return "Tracked today";
  return RELATIVE_DAYS.includes(dayLabel)
    ? `Tracked ${dayLabel.toLowerCase()}`
    : `Tracked on ${dayLabel}`;
}

/**
 * The summary band: Tracked today with its split by Accelo task, and Ready to
 * publish with its count and its warning chip. The Publish button sits in the
 * rows card.
 */
export function SummaryBand({
  totalMs,
  runningCount,
  pausedCount,
  segments,
  ready,
  dayLabel,
}: SummaryBandProps) {
  const isEmpty = ready.total === 0;
  const heading = trackedHeading(dayLabel);

  return (
    <div className={styles.band}>
      <section className={styles.card} aria-label={heading}>
        <div className={styles.trackedHead}>
          <div className={styles.figure}>
            <span className={styles.label}>{heading}</span>
            <span className={`${styles.total} ${isEmpty ? styles.muted : ""}`}>
              {formatElapsed(totalMs)}
            </span>
          </div>
          {!isEmpty && (
            <div className={styles.states}>
              <span className={styles.state}>
                <span className={`${styles.dot} ${styles.dotRunning}`} />
                {runningCount} running
              </span>
              <span className={styles.state}>
                <span className={`${styles.dot} ${styles.dotPaused}`} />
                {pausedCount} paused
              </span>
            </div>
          )}
        </div>

        {isEmpty ? (
          <>
            <div className={styles.emptyBar} />
            <span className={styles.note}>The split appears once a row carries a task.</span>
          </>
        ) : (
          <>
            <div className={styles.bar} aria-hidden="true">
              {segments.map((segment) => (
                <span
                  key={segment.key}
                  className={styles.segment}
                  style={{
                    flexGrow: Math.max(1, Math.round(segment.ms / 1000)),
                    background: segment.colour,
                  }}
                />
              ))}
            </div>
            <ul className={styles.legend}>
              {segments.map((segment) => (
                <li key={segment.key} className={styles.legendItem}>
                  <span className={styles.swatch} style={{ background: segment.colour }} />
                  <span className={styles.legendLabel}>{segment.label}</span>
                  <span className={styles.legendTime}>{formatElapsed(segment.ms)}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <section className={`${styles.card} ${styles.readyCard}`} aria-label="Ready to publish">
        <span className={styles.label}>Ready to publish</span>
        <span className={`${styles.readyFigure} ${isEmpty ? styles.muted : ""}`}>
          {ready.ready} of {ready.total}
        </span>
        {ready.short > 0 && (
          <span className={styles.chip}>
            <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.chipIcon}>
              <path d="M12 6.5v7" />
              <path d="M12 17.4v.2" />
            </svg>
            {ready.short === 1
              ? "1 row needs a task or a name"
              : `${ready.short} rows need a task or a name`}
          </span>
        )}
        {!isEmpty && ready.short === 0 && (
          <span className={styles.note}>Every row carries a name and a task.</span>
        )}
      </section>
    </div>
  );
}
